import { reportEvent } from '@/lib/monitoring'
import { isServiceError, ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import type { CardInput, PagosService, PaymentOutcome, WaitOptions } from '../pagos/types'
import type { Money } from '../types'
import type { ChangeConfirmation, ChangeQuote, ChangeResult, PendingChangePayment, Stay } from './types'

/**
 * Cambio con coste en el orden de la app actual (inventario D45): ModifyBookingView aplica el cambio con
 * `modifyreserve_guestapp` (E12) y la diferencia queda en `pendingPrice`, que se cobra con el pago "Total"
 * (PaymentTotal.tsx: E2 → E3 con `paidTypeRestId` y `price = pendingPrice` → iframe del banco → E4).
 *
 * v2 hace esas mismas llamadas seguidas y añade dos mitigaciones en cliente:
 * 1. Si el cobro falla o se rechaza en el banco, se intenta volver a las fechas originales con E12 (`revert`).
 * 2. Si la reversión falla o no puede intentarse (el banco no ha respondido), el cambio queda registrado en el
 *    dispositivo (D22) como "pendiente de pago" hasta que E2 no tenga nada pendiente.
 *
 * Con diferencia a favor, como la app actual: solo E12, sin llamada de devolución ni promesa de importe o plazo.
 * Real y mock comparten esta orquestación; cambian `apply`, `revert` y el servicio de pagos.
 */
export interface ChangePaymentBackend {
  pagos: PagosService
  /** Aplica el cambio cotizado y devuelve la reserva ya cambiada (real: E12). */
  apply(stay: Stay, quote: ChangeQuote, lang: string): Promise<Stay>
  /** Vuelve a las fechas originales (real: E12) y devuelve la reserva revertida. */
  revert(stay: Stay, pending: PendingChangePayment, lang: string): Promise<Stay>
  getStay(guestCode: string, lang: string): Promise<Stay>
}

const PENDING_FIELD = 'pendingChangePayment'
const RESULTS_FIELD = 'changeResults'

type StoredResult = Omit<ChangeResult, 'stay'>
interface StoredPending extends PendingChangePayment {
  temporalPaidId: string | null
  cardLast4: string | null
}

export const readPendingChange = (guestCode: string): StoredPending | null => readLocal<StoredPending>(guestCode, PENDING_FIELD)
const writePendingChange = (guestCode: string, value: StoredPending | null) => writeLocal(guestCode, PENDING_FIELD, value)
const readResults = (guestCode: string) => readLocal<Record<string, StoredResult>>(guestCode, RESULTS_FIELD) ?? {}
const writeResult = (guestCode: string, changeId: string, result: StoredResult) => writeLocal(guestCode, RESULTS_FIELD, { ...readResults(guestCode), [changeId]: result })

const pad = (n: number) => String(n).padStart(2, '0')
const dayOf = (iso: string) => {
  const d = new Date(iso)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function lastFour(card: CardInput, savedLast4: string | null): string | null {
  if (card.kind === 'saved') return savedLast4
  const digits = card.number.replace(/\D/g, '')
  return digits.length >= 4 ? digits.slice(-4) : null
}

function reasonOf(e: unknown): 'card' | 'generic' | 'already_paid' | 'offline' {
  if (!isServiceError(e)) return 'generic'
  if (e.code === 'offline') return 'offline'
  if (e.code === 'already_paid') return 'already_paid'
  if (e.code === 'payment_failed' && e.message === 'card') return 'card'
  return 'generic'
}

function stored(quote: ChangeQuote, paid: Money | null, cardLast4: string | null): StoredResult {
  return { type: quote.input.type, paid, cardLast4: paid ? cardLast4 : null, refund: null, receiptUrl: null, confirmationSentTo: null, updated: [] }
}

export async function confirmWithPayment(backend: ChangePaymentBackend, stay: Stay, quote: ChangeQuote, card: CardInput | null, lang: string): Promise<ChangeConfirmation> {
  const changeId = `CHG-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  // 1. E12: si falla, no ha cambiado nada y el error sube tal cual.
  const updated = await backend.apply(stay, quote, lang)
  const done = (paid: Money | null, last4: string | null): ChangeConfirmation => {
    const result = stored(quote, paid, last4)
    writeResult(stay.guestCode, changeId, result)
    return { status: 'applied', result: { ...result, stay: updated } }
  }
  // Diferencia a favor o sin diferencia: la app actual no hace nada más.
  if (quote.difference.amount <= 0) return done(null, null)

  const pending: StoredPending = {
    changeId,
    type: quote.input.type,
    originalCheckIn: dayOf(stay.dates.checkIn),
    originalCheckOut: dayOf(stay.dates.checkOut),
    newCheckIn: updated.dates.checkIn,
    newCheckOut: updated.dates.checkOut,
    amount: quote.pendingAfter ?? quote.difference,
    createdAt: new Date().toISOString(),
    temporalPaidId: null,
    cardLast4: null,
  }
  // 2. E2 recién pedido: lo que cobra el pago "Total" es lo pendiente tras el cambio.
  let summary
  try {
    summary = await backend.pagos.getPaymentSummary(updated, lang)
  } catch (e) {
    writePendingChange(stay.guestCode, pending)
    reportEvent('change_payment_not_started', { changeType: quote.input.type, step: 'E2', code: reasonOf(e) })
    return { status: 'payment_failed', changeId, amount: pending.amount, reason: reasonOf(e) }
  }
  if (summary.pending.amount <= 0) return done(null, null)
  const amount = summary.pending
  const last4 = card ? lastFour(card, summary.savedCard?.last4 ?? null) : null
  writePendingChange(stay.guestCode, { ...pending, amount, cardLast4: last4 })
  if (!card) {
    reportEvent('change_payment_not_started', { changeType: quote.input.type, step: 'card', code: 'generic' })
    return { status: 'payment_failed', changeId, amount, reason: 'generic' }
  }

  // 3. E3 (dentro de pagos.pay, con E2 y la comprobación de "ya pagado" de la app actual).
  let start
  try {
    start = await backend.pagos.pay(updated, { kind: 'rest', amount, card }, lang)
  } catch (e) {
    reportEvent('change_payment_not_started', { changeType: quote.input.type, step: 'E3', code: reasonOf(e) })
    return { status: 'payment_failed', changeId, amount, reason: reasonOf(e) }
  }
  if (start.status === 'completed') {
    writePendingChange(stay.guestCode, null)
    return done(amount, last4)
  }
  writePendingChange(stay.guestCode, { ...pending, amount, cardLast4: last4, temporalPaidId: start.temporalPaidId })
  return { status: 'redirect', changeId, iframeUrl: start.iframeUrl, temporalPaidId: start.temporalPaidId, amount }
}

export async function waitForChangePayment(backend: ChangePaymentBackend, stay: Stay, changeId: string, options: WaitOptions = {}): Promise<PaymentOutcome> {
  if (readResults(stay.guestCode)[changeId]) return 'completed'
  const pending = readPendingChange(stay.guestCode)
  if (!pending || pending.changeId !== changeId || !pending.temporalPaidId) return 'rejected'
  // 4. E4 como cualquier pago (D34). El registro se mantiene hasta saber que se ha cobrado.
  const outcome = await backend.pagos.waitForResult(stay, pending.temporalPaidId, options)
  if (outcome === 'rejected') reportEvent('change_payment_rejected', { changeType: pending.type, outcome })
  if (outcome === 'timeout') reportEvent('change_payment_no_answer', { changeType: pending.type, outcome })
  if (outcome === 'completed') {
    writePendingChange(stay.guestCode, null)
    writeResult(stay.guestCode, changeId, { type: pending.type, paid: pending.amount, cardLast4: pending.cardLast4, refund: null, receiptUrl: null, confirmationSentTo: null, updated: [] })
  }
  return outcome
}

export async function changeResult(backend: ChangePaymentBackend, stay: Stay, changeId: string, lang: string): Promise<ChangeResult> {
  const result = readResults(stay.guestCode)[changeId]
  if (!result) throw new ServiceError('server', 'change not completed')
  return { ...result, stay: await backend.getStay(stay.guestCode, lang) }
}

export async function revertChangePayment(backend: ChangePaymentBackend, stay: Stay, changeId: string, lang: string): Promise<Stay> {
  const pending = readPendingChange(stay.guestCode)
  if (!pending || pending.changeId !== changeId) throw new ServiceError('server', 'no pending change to revert')
  let reverted: Stay
  try {
    reverted = await backend.revert(stay, pending, lang)
  } catch (e) {
    reportEvent('change_revert_failed', { changeType: pending.type, code: isServiceError(e) ? e.code : 'unknown' })
    throw e
  }
  writePendingChange(stay.guestCode, null)
  return reverted
}

/** El registro solo vale mientras E2 tenga algo pendiente; si ya no, se da por pagado (o resuelto por la propiedad). */
export async function pendingChange(backend: ChangePaymentBackend, stay: Stay, lang: string, signal?: AbortSignal): Promise<PendingChangePayment | null> {
  const pending = readPendingChange(stay.guestCode)
  if (!pending) return null
  try {
    const summary = await backend.pagos.getPaymentSummary(stay, lang, signal)
    if (summary.pending.amount <= 0) {
      writePendingChange(stay.guestCode, null)
      return null
    }
    return { ...strip(pending), amount: summary.pending }
  } catch {
    return strip(pending)
  }
}

function strip(p: StoredPending): PendingChangePayment {
  return { changeId: p.changeId, type: p.type, originalCheckIn: p.originalCheckIn, originalCheckOut: p.originalCheckOut, newCheckIn: p.newCheckIn, newCheckOut: p.newCheckOut, amount: p.amount, createdAt: p.createdAt }
}
